import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import {
  getCurrentClientProfile,
  isIndividualClient,
  isEnterpriseClient,
} from '../../services/ClientService';
import { useAuth } from '../../hooks/useAuth';
import IndividualProfileForm from './IndividualProfileForm';
import EnterpriseProfileForm from './EnterpriseProfileForm';

/**
 * Página de perfil do usuário
 * Permite visualizar e editar os dados do cliente, seja pessoa física ou jurídica
 */
export default function ProfilePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  // Determinar o tipo de cliente
  const isIndividual = profileData ? isIndividualClient(profileData) : false;
  const isEnterprise = profileData ? isEnterpriseClient(profileData) : false;

  // Carregar os dados do perfil do cliente
  useEffect(() => {
    // Verificar se está autenticado
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Função para carregar o perfil
    const loadProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await getCurrentClientProfile();
        setProfileData(data);
      } catch (error) {
        console.error('[ProfilePage] Erro ao carregar perfil:', error);
        setError('Não foi possível carregar os dados do perfil. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [isAuthenticated, navigate]);

  // Função para lidar com a mudança de abas
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Função para lidar com o sucesso na atualização do perfil
  const handleUpdateSuccess = (message) => {
    setSuccessMessage(message || 'Perfil atualizado com sucesso!');
    setError('');

    // Recarregar os dados do perfil após atualização
    getCurrentClientProfile()
      .then((data) => setProfileData(data))
      .catch((err) => console.error('[ProfilePage] Erro ao recarregar perfil:', err));

    // Rolar para o topo para mostrar a mensagem de sucesso
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Função para lidar com erros na atualização do perfil
  const handleUpdateError = (error) => {
    setSuccessMessage('');
    setError(error.message || 'Ocorreu um erro ao atualizar o perfil. Por favor, tente novamente.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Renderização condicional com base no estado de carregamento
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando perfil...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: '#f5f5f5',
        py: 4,
        px: 2,
      }}
    >
      <Paper elevation={3} sx={{ width: '100%', maxWidth: 800, p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Meu Perfil
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!profileData && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Não foi possível carregar os dados do perfil.
          </Alert>
        )}

        {profileData && (
          <>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="Abas do perfil"
              sx={{ mb: 3 }}
            >
              <Tab icon={<PersonIcon />} label="Perfil" id="profile-tab" />
              <Tab icon={<SecurityIcon />} label="Segurança" id="security-tab" disabled />
            </Tabs>

            <Box role="tabpanel" hidden={tabIndex !== 0} id="profile-panel">
              {tabIndex === 0 && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Tipo de Conta
                    </Typography>
                    <Typography variant="body1">
                      {isIndividual
                        ? '👤 Pessoa Física'
                        : isEnterprise
                          ? '🏢 Pessoa Jurídica'
                          : '❓ Tipo não identificado'}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {isIndividual && (
                    <IndividualProfileForm
                      profileData={profileData}
                      onSuccess={handleUpdateSuccess}
                      onError={handleUpdateError}
                    />
                  )}

                  {isEnterprise && (
                    <EnterpriseProfileForm
                      profileData={profileData}
                      onSuccess={handleUpdateSuccess}
                      onError={handleUpdateError}
                    />
                  )}

                  {!isIndividual && !isEnterprise && (
                    <Alert severity="error">
                      Tipo de conta não reconhecido. Entre em contato com o suporte.
                    </Alert>
                  )}
                </>
              )}
            </Box>

            <Box role="tabpanel" hidden={tabIndex !== 1} id="security-panel">
              {tabIndex === 1 && (
                <Box>
                  <Typography variant="body1">
                    Configurações de segurança da conta (em breve).
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/map')}
            sx={{ mr: 2 }}
          >
            Voltar para o Mapa
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
